import {
  createDocumentAssemblerTemplate,
  DOCUMENT_ASSEMBLER_LIMITS,
  IDocumentAssemblerTemplateCategory,
} from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  Textarea,
} from '@commenda/kakashi';
import {
  messageFromApiErrorResponse,
  readJsonResponseAs,
} from '@commenda/utils';
import { useState } from 'react';
import { useNotification } from '../hooks/useNotification';
import { DocumentAssemblerCategoryPicker } from './DocumentAssemblerCategoryPicker';

import { DocumentAssemblerHelper } from './DocumentAssemblerHelper';

interface DocumentAssemblerTemplateCreateDialogProps {
  /** A category is global across every customer, so only Commenda staff may add one from here. */
  canCreateCategory?: boolean;
  onClose: () => void;
  onCreated?: (templateId: string) => void;
}

export const DocumentAssemblerTemplateCreateDialog = ({
  onClose,
  onCreated,
  canCreateCategory = false,
}: DocumentAssemblerTemplateCreateDialogProps) => {
  const { setNotification } = useNotification();

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [isKeyEdited, setIsKeyEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [category, setCategory] =
    useState<IDocumentAssemblerTemplateCategory | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onNameChange = (nextName: string) => {
    setName(nextName);
    if (!isKeyEdited)
      setKey(DocumentAssemblerHelper.suggestKeyFromName(nextName));
  };

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isKeyValid = DocumentAssemblerHelper.isValidKey(key);
  const canSave =
    Boolean(trimmedName) &&
    Boolean(trimmedDescription) &&
    Boolean(category) &&
    isKeyValid &&
    !isSaving;

  const onSubmit = async () => {
    if (!category) return;

    setSubmitError(null);
    setIsSaving(true);

    const response = await createDocumentAssemblerTemplate({
      key,
      title: trimmedName,
      description: trimmedDescription,
      categories: [category.key],
    });

    if (!response.ok) {
      setSubmitError(
        await messageFromApiErrorResponse(
          response,
          'The template could not be created.'
        )
      );
      setIsSaving(false);
      return;
    }

    const body = await readJsonResponseAs<{ template: { id: string } }>(
      response
    );
    setIsSaving(false);

    if (!body?.template?.id) {
      setSubmitError('The template was created but could not be read back.');
      return;
    }

    setNotification({ show: true, valid: true, title: 'Template created' });
    onCreated?.(body.template.id);
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="flex-col items-start gap-xs">
          <DialogTitle>Create a template</DialogTitle>
          <DialogDescription>
            A template holds the versions of one kind of document. Upload its
            first Word file after creating it.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-xl p-3xl">
          {submitError ? (
            <Alert variant="destructive" description={submitError} />
          ) : null}

          <FieldWrapper>
            <Label htmlFor="document-assembler-template-name" required>
              Name
            </Label>
            <Input
              id="document-assembler-template-name"
              value={name}
              maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxNameLength}
              placeholder="Distribution agreement"
              onChange={(event) => onNameChange(event.target.value)}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label htmlFor="document-assembler-template-key" required>
              Key
            </Label>
            <Input
              id="document-assembler-template-key"
              value={key}
              maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxKeyLength}
              placeholder="ica.distribution"
              error={
                key && !isKeyValid
                  ? 'Lower-case letters, numbers, and a single -, . or _ between them.'
                  : undefined
              }
              onChange={(event) => {
                setIsKeyEdited(true);
                setKey(event.target.value);
              }}
            />
            <p className="txt-extra-small-regular mt-xs text-text-secondary">
              A key never changes once it is set.
            </p>
          </FieldWrapper>

          <DocumentAssemblerCategoryPicker
            selectedCategoryKey={category?.key ?? ''}
            onSelect={setCategory}
            label="Category"
            isRequired
            canCreate={canCreateCategory}
            isInsideDialog
            hint={
              canCreateCategory
                ? 'The family of document this is. Pick one that exists, or add a new one — a catalogue with three spellings of the same family hides templates from the agent that needs them.'
                : 'The family of document this is.'
            }
          />

          <FieldWrapper>
            <Label htmlFor="document-assembler-template-description" required>
              Description
            </Label>
            <Textarea
              id="document-assembler-template-description"
              rows={4}
              value={description}
              maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxDescriptionLength}
              onChange={(event) => setDescription(event.target.value)}
            />
            <p className="txt-extra-small-regular mt-xs text-text-secondary">
              The expert agent picks a template by reading this. Say what it
              covers <strong>and what it does not</strong>&nbsp;— a description
              that overreaches gets the wrong template chosen.
            </p>
          </FieldWrapper>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" size="sm" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            type="button"
            disabled={!canSave}
            isLoading={isSaving}
            onClick={onSubmit}
          >
            Create template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
